import { fetchCatalogsTodayCountApi, fetchResultsApi, SearchAuctionFacet } from '@/redux/api/auctionCalendar';
import {
    LOAD_CATALOG_SEARCH_FAIL,
    LOAD_CATALOG_SEARCH_REQUEST,
    LOAD_CATALOG_SEARCH_SUCCESS,
    LOAD_CATALOGS_TODAY_COUNT_FAIL,
    LOAD_CATALOGS_TODAY_COUNT_REQUEST,
    LOAD_CATALOGS_TODAY_COUNT_SUCCESS,
    UPDATE_FACETS_SELECTED,
} from './actions';
import { createSelector } from 'reselect';
import { fetchCatalogAdIfNeeded } from './catalogAd';
import { fetchCatalogsIfNeeded } from './catalog';
import { fetchCoverLotsIfNeeded } from './coverLots';
import { getDeployment } from './config';
import { getUserExcludedHouses } from './searchExclusions';
import { GlobalState } from '../rootReducer';

export interface State {
    catalogIds: number[];
    error: boolean;
    facets: SearchAuctionFacet[];
    submitted: boolean;
    success: boolean;
    totalFound: number;
}

// reducer
const DEFAULT_STATE = {
    catalogIds: [],
    error: false,
    facets: [],
    submitted: false,
    success: false,
    totalFound: 0,
};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case LOAD_CATALOG_SEARCH_FAIL:
            return {
                ...state,
                error: true,
                submitted: false,
                success: false,
            };
        case LOAD_CATALOG_SEARCH_REQUEST:
            return {
                ...state,
                submitted: true,
                success: false,
            };
        case LOAD_CATALOG_SEARCH_SUCCESS:
            return {
                ...state,
                catalogIds: action.payload.results.map((c) => c.catid),
                facets: action.payload.facets,
                submitted: false,
                success: true,
                totalFound: action.payload.count,
            };
        case UPDATE_FACETS_SELECTED:
            return {
                ...state,
                facets: action.payload.facets,
            };
        case LOAD_CATALOGS_TODAY_COUNT_REQUEST:
            return {
                ...state,
                submitted: true,
                success: false,
            };
        case LOAD_CATALOGS_TODAY_COUNT_SUCCESS:
            return {
                ...state,
                catalogsTodayCount: action.payload.count,
                submitted: false,
                success: true,
            };
        case LOAD_CATALOGS_TODAY_COUNT_FAIL:
            return {
                ...state,
                error: true,
                submitted: false,
                success: false,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.auctionCalendar;

export const getUiAuctionCalendar = createSelector(stateSelector, (state) => state);

// @ts-ignore
export const getCatalogsTodayCount = createSelector(stateSelector, (state) => state.catalogsTodayCount);

export const getAuctionCalendarIds = createSelector(stateSelector, (state) => state.catalogIds);

export const getAuctionCalendarTotalCount = createSelector(stateSelector, (state) => state.totalFound);

export const getAuctionCalendarSubmitted = createSelector(stateSelector, (state) => state.submitted);

export const getAuctionCalendarFacets = createSelector(stateSelector, (state) =>
    state.facets.length > 0 ? state.facets : []
);

export const getAuctionCalendarHouseFacets = createSelector(getAuctionCalendarFacets, (facets) => {
    const facet = facets.find((x) => x.id === 'house_id');
    if (facet && facet.values && facet.values.length > 0) {
        return facet.values.sort(sortFacets);
    }
    return [];
});

export const getAuctionCalendarCountryFacets = createSelector(getAuctionCalendarFacets, (facets) => {
    const facet = facets.find((x) => x.id === 'country');
    if (facet && facet.values && facet.values.length > 0) {
        return facet.values.sort(sortFacets);
    }
    return [];
});

export const getAuctionCalendarSaleTypeFacets = createSelector(getAuctionCalendarFacets, (facets) => {
    const facet = facets.find((x) => x.id === 'sale_type');
    if (facet && facet.values && facet.values.length > 0) {
        return facet.values.sort(sortFacets);
    }
    return [];
});

const sortFacets = (a, b) => {
    if (a.selected === b.selected) {
        return b.count - a.count;
    }
    return b.selected ? 1 : -1;
};

/* ACTIONS CREATORS */
export const fetchAuctionCalendarResults = (queryParams: any) => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const deployment = getDeployment(state);
        const searchExclusions = getUserExcludedHouses(state);

        const auctionCalendarQuery = {
            client: 'web',
            client_version: '5.0.0',
            excludedHouses: JSON.stringify(searchExclusions),
            max_facet_values: 3,
            pagesize: queryParams.pageSize || queryParams.pagesize,
            ...queryParams,
        };

        dispatch({
            type: LOAD_CATALOG_SEARCH_REQUEST,
        });

        const response = await fetchResultsApi({ deployment, queryParams: auctionCalendarQuery });
        dispatch({
            payload: response.payload,
            type: LOAD_CATALOG_SEARCH_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: LOAD_CATALOG_SEARCH_FAIL,
        });
    }
};

export const fetchCatalogsTodayCountIfNeeded = () => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const todayCount = getCatalogsTodayCount(state);
        if (!todayCount) {
            const deployment = getDeployment(state);

            dispatch({
                type: LOAD_CATALOGS_TODAY_COUNT_REQUEST,
            });
            const response = await fetchCatalogsTodayCountApi({ deployment });
            dispatch({
                payload: response.data[0],
                type: LOAD_CATALOGS_TODAY_COUNT_SUCCESS,
            });
        }
    } catch (error) {
        dispatch({
            error: true,
            type: LOAD_CATALOGS_TODAY_COUNT_FAIL,
        });
    }
};

export const updateFacetsSelected = () => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const facets = getAuctionCalendarFacets(state);

    dispatch({
        payload: { facets },
        type: UPDATE_FACETS_SELECTED,
    });
};

export const fetchDataForAuctionCalendarPage = (queryParams: any) => async (dispatch: Function, getState: Function) => {
    return Promise.all([
        dispatch(fetchCatalogsTodayCountIfNeeded()),
        dispatch(fetchCatalogAdIfNeeded({ categoryIds: '0', pageName: 'calendar' })), // pass 0 to indicate that its the calendar/catalog search page
    ])
        .then(() => Promise.all([dispatch(fetchAuctionCalendarResults(queryParams))])) // in order to re-arrange the facets set in state we need to adjust them after the search call
        .then(() => Promise.all([dispatch(updateFacetsSelected())]))
        .then(() => {
            const state = getState();
            const catalogIds = getAuctionCalendarIds(state);
            return Promise.all([
                dispatch(fetchCatalogsIfNeeded(catalogIds)),
                dispatch(fetchCoverLotsIfNeeded(catalogIds)),
            ]);
        });
};
